import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Fade, Stack, useTheme } from '@mui/material'
import { bbox, featureCollection as createFeatureCollection } from '@turf/turf'
import { FeatureCollection } from 'geojson'
import { Layer, LngLatBoundsLike, Source, useMap } from 'react-map-gl/dist/es5'
import { Expression, GeoJSONSource } from 'mapbox-gl'
import { useMapPin } from '../../../hooks'
import { MapTheme } from '../const'
import { LayerInteractionContext } from '../../LayerInteractionContext'
import { BEFORE_ID } from './const'

interface FieldsLayerProps {
  featureCollection?: FeatureCollection
  containMap?: boolean
  disableRecenter?: boolean
  zoomToField?: boolean
  zoomToCluster?: boolean
  onFieldClicked?: (resourceId?: string) => void
}

const IDS = {
  CLUSTER: 'field-clusters',
  CLUSTER_TEXT: 'field-cluster-count',
  FIELD_PIN: 'field-pin',
  FIELD_BOUNDARY: 'field-boundary',
  FIELD_FILL: 'field-fill',
  SOURCE: 'field-geojson',
}

export const FIELDS_LAYER_INTERACTIVE_IDS = [
  IDS.CLUSTER,
  IDS.FIELD_PIN,
  IDS.FIELD_FILL,
  IDS.FIELD_BOUNDARY,
]

export const FieldsLayer = ({
  containMap,
  disableRecenter,
  featureCollection,
  onFieldClicked,
  zoomToField = true,
  zoomToCluster = true,
}: FieldsLayerProps) => {
  const { t } = useTranslation('@cibo/mapgl/FieldsLayer')
  const [moved, setMoved] = useState(false)
  const { current: map } = useMap()
  const { palette, spacing } = useTheme<MapTheme>()

  const fillColorExpression = useMemo(
    () => [
      'match',
      ['get', 'status'],
      'needs_info',
      palette.pinWarningFill,
      'enrolled',
      palette.pinSuccessFill,
      'qualified',
      palette.pinInfoFill,
      palette.pinOtherFill,
    ],
    [palette]
  )
  const strokeColorExpression = useMemo(
    () => [
      'match',
      ['get', 'status'],
      'needs_info',
      palette.pinWarningStroke,
      'enrolled',
      palette.pinSuccessStroke,
      'qualified',
      palette.pinInfoStroke,
      palette.pinOtherStroke,
    ],
    [palette]
  )

  const warningPin = useMapPin('needs_info', palette.pinWarningFill, palette.pinWarningStroke)
  const infoPin = useMapPin('qualified', palette.pinInfoFill, palette.pinInfoStroke)
  const successPin = useMapPin('enrolled', palette.pinSuccessFill, palette.pinSuccessStroke)
  const otherPin = useMapPin('other', palette.pinOtherFill, palette.pinOtherStroke)

  const pinExpression = useMemo(
    () => [
      'match',
      ['get', 'status'],
      'needs_info',
      warningPin,
      'enrolled',
      successPin,
      'qualified',
      infoPin,
      otherPin,
    ],
    [warningPin, infoPin, successPin, otherPin]
  )

  const { clickedFeatures } = useContext(LayerInteractionContext)

  const fitBoundsOptions = useMemo(
    () => ({
      padding: typeof spacing === 'function' ? parseInt(`${spacing(8)}`, 10) : 0,
      bearing: 0,
      pitch: 0,
    }),
    [spacing]
  )

  const [clickedField, setClickedField] = useState<string>()

  useEffect(() => {
    if (onFieldClicked) onFieldClicked(clickedField)
  }, [clickedField])

  useEffect(() => {
    if (!clickedFeatures && map) {
      setClickedField(undefined)
    }
    if (!!clickedFeatures && map) {
      const firstField = clickedFeatures.find(({ layer: { id } }) =>
        [IDS.FIELD_FILL, IDS.FIELD_PIN].includes(id)
      )
      if (firstField) {
        if (zoomToField && firstField.properties?.bbox) {
          map.fitBounds(firstField.properties.bbox.split(','), fitBoundsOptions)
        }
        if (onFieldClicked && firstField.properties?.resourceId) {
          setClickedField(firstField.properties?.resourceId)
        }
      }

      if (zoomToCluster) {
        const firstCluster = clickedFeatures.find(({ layer: { id } }) => id === IDS.CLUSTER)
        if (firstCluster?.geometry.type === 'Point') {
          if (!firstCluster.id) return

          const clusterSource = map.getSource(IDS.SOURCE) as GeoJSONSource

          clusterSource.getClusterChildren(firstCluster.id as number, (err, features) => {
            if (!features) return
            const clusterBounds = bbox(createFeatureCollection(features))
            map.fitBounds(clusterBounds as LngLatBoundsLike, {
              ...fitBoundsOptions,
              maxZoom: 14,
            })
          })
        }
      }
    }
  }, [clickedFeatures, map, onFieldClicked, zoomToCluster, zoomToField])

  useEffect(() => {
    if (!!containMap && !!featureCollection?.features?.length && !!map) {
      map.fitBounds(bbox(featureCollection) as LngLatBoundsLike, {
        ...fitBoundsOptions,
        animate: false,
      })
      map.on('moveend', () => setMoved(true))
    }
  }, [map, featureCollection?.features.length, containMap])

  const handleRecenter = useCallback(() => {
    if (!!containMap && !!featureCollection?.features?.length && !!map) {
      map.fitBounds(bbox(featureCollection) as LngLatBoundsLike, fitBoundsOptions)
      map.once('moveend', () => setMoved(false))
    }
  }, [map, featureCollection, containMap])

  const [points, shapes] = useMemo(
    () => [
      createFeatureCollection(
        featureCollection?.features?.filter(({ geometry: { type } }) => type === 'Point') || []
      ),
      createFeatureCollection(
        featureCollection?.features?.filter(({ geometry: { type } }) => type !== 'Point') || []
      ),
    ],
    [featureCollection]
  )

  if (!infoPin) {
    return null
  }

  return (
    <>
      <Source type="geojson" data={points} cluster clusterRadius={25} id={IDS.SOURCE}>
        <Layer
          id={IDS.CLUSTER}
          type="circle"
          filter={['has', 'point_count']}
          paint={{
            'circle-color': palette.resultColor.main,
            'circle-radius': ['step', ['get', 'point_count'], 10, 50, 10, 100, 15, 1000, 20],
            'circle-opacity': ['interpolate', ['linear'], ['zoom'], 0, 1, 10, 1, 10.75, 0],
          }}
          beforeId={BEFORE_ID}
        />

        <Layer
          id={IDS.CLUSTER_TEXT}
          type="symbol"
          filter={['has', 'point_count']}
          layout={{
            'text-field': '{point_count_abbreviated}',
            'text-size': 12,
            'text-font': ['Nunito Sans ExtraBold'],
            'text-allow-overlap': true,
          }}
          paint={{
            'text-color': palette.resultColor.contrastText,
            'text-opacity': ['interpolate', ['linear'], ['zoom'], 0, 1, 10, 1, 10.75, 0],
          }}
          beforeId={BEFORE_ID}
        />

        <Layer
          type="symbol"
          id={IDS.FIELD_PIN}
          layout={{
            'icon-image': pinExpression as Expression,
            'icon-size': 0.5,
            'icon-allow-overlap': true,
            'icon-anchor': 'bottom',
          }}
          filter={['!', ['has', 'point_count']]}
          paint={{ 'icon-opacity': ['interpolate', ['linear'], ['zoom'], 0, 1, 10, 1, 10.75, 0] }}
          beforeId={BEFORE_ID}
        />
      </Source>

      <Source type="geojson" data={shapes}>
        <Layer
          type="fill"
          id={IDS.FIELD_FILL}
          paint={{
            'fill-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0, 10, 0, 10.5, 0.3],
            'fill-color': fillColorExpression as Expression,
          }}
          beforeId={BEFORE_ID}
        />
        <Layer
          type="line"
          id={IDS.FIELD_BOUNDARY}
          paint={{
            'line-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0, 10, 0, 10.5, 1],
            'line-color': strokeColorExpression as Expression,
            'line-width': 3,
          }}
          beforeId={BEFORE_ID}
        />
      </Source>

      {containMap && !disableRecenter && (
        <Fade in={moved}>
          <Stack padding={2}>
            <Box>
              <Button variant="contained" onClick={handleRecenter}>
                {t('recenter')}
              </Button>
            </Box>
          </Stack>
        </Fade>
      )}
    </>
  )
}
